import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './GoogleMalwareHistory.css';

const GoogleMalwareHistory = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchReports(currentPage);
  }, [currentPage, filterStatus]);

  const fetchReports = async (page) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_URL;
      
      let url = `${API_BASE_URL}/google-malware?page=${page}&limit=10`;
      if (filterStatus !== 'all') {
        url += `&status=${filterStatus}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setReports(data.reports);
        setPagination({
          currentPage: data.currentPage,
          totalPages: data.totalPages,
          totalReports: data.totalReports,
          hasNextPage: data.currentPage < data.totalPages,
          hasPrevPage: data.currentPage > 1
        });
      } else {
        console.error('Failed to fetch reports');
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
    setLoading(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Submitted to Google':
        return 'success';
      case 'Pending':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const closeModal = () => {
    setSelectedReport(null);
  };

  const handleMarkAsSubmitted = async (caseNumber) => {
    try {
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_URL;

      const response = await fetch(
        `${API_BASE_URL}/google-malware/report/${caseNumber}/submitted`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        alert('Report marked as submitted to Google!');
        fetchReports(currentPage);
        closeModal();
      } else {
        alert('Failed to update report status');
      }
    } catch (error) {
      console.error('Error updating report:', error);
      alert('An error occurred while updating the report');
    }
  };

  return (
    <div className="google-malware-history">
      <div className="history-header">
        <div className="header-content">
          <button onClick={() => navigate('/tools')} className="back-btn">
            ← Back to Tools
          </button>
          <div className="header-info">
            <h1>Google Malware Report History</h1>
            <p>View and track your Android app malware submissions to Google Play Protect</p>
          </div>
          <button onClick={() => navigate('/google-malware')} className="new-submission-btn">
            + New Report
          </button>
        </div>
      </div>

      <div className="history-container">
        {/* Filter Status */}
        <div className="filter-section">
          <label htmlFor="status-filter">Filter by Status:</label>
          <select
            id="status-filter"
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="all">All Reports</option>
            <option value="Pending">Pending</option>
            <option value="Submitted to Google">Submitted to Google</option>
          </select>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="spinner-large"></div>
            <p>Loading report history...</p>
          </div>
        ) : reports.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3>No Reports Yet</h3>
            <p>
              {filterStatus === 'all'
                ? "You haven't submitted any malware reports yet."
                : `No reports with status "${filterStatus}".`}
            </p>
            <button onClick={() => navigate('/google-malware')} className="primary-btn">
              Submit Your First Report
            </button>
          </div>
        ) : (
          <>
            <div className="reports-table-container">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Case Number</th>
                    <th>App Package(s)</th>
                    <th>PHA Category</th>
                    <th>Status</th>
                    <th>Submitted At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report, index) => (
                    <tr key={report._id} className="report-row">
                      <td className="serial-number">
                        {(currentPage - 1) * 10 + index + 1}
                      </td>
                      <td className="case-number-cell">
                        <code className="case-number">{report.caseNumber}</code>
                      </td>
                      <td className="packages-cell">
                        <div className="packages-preview">
                          <span className="primary-package">{report.packageNames[0]}</span>
                          {report.packageNames.length > 1 && (
                            <span className="package-count">
                              +{report.packageNames.length - 1} more
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="category-cell">
                        <span className="category-badge">{report.suspectedCategory}</span>
                      </td>
                      <td className="status-cell">
                        <span className={`status-badge ${getStatusColor(report.status)}`}>
                          {report.status}
                        </span>
                      </td>
                      <td className="date-cell">
                        <div className="date-info">
                          <div className="date">
                            {formatDate(report.createdAt).split(' at ')[0]}
                          </div>
                          <div className="time">
                            {formatDate(report.createdAt).split(' at ')[1]}
                          </div>
                        </div>
                      </td>
                      <td className="actions-cell">
                        <button
                          onClick={() => setSelectedReport(report)}
                          className="view-details-btn"
                          title="View Details"
                        >
                          👁️ View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination && pagination.totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={!pagination.hasPrevPage}
                  className="pagination-btn"
                >
                  Previous
                </button>

                <div className="pagination-info">
                  Page {pagination.currentPage} of {pagination.totalPages}
                  {pagination.totalReports && (
                    <span className="total-info"> (Total: {pagination.totalReports})</span>
                  )}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={!pagination.hasNextPage}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Report Details Modal */}
      {selectedReport && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Report Details</h3>
              <button onClick={closeModal} className="close-btn">×</button>
            </div>

            <div className="modal-body">
              <div className="report-info">
                <div className="info-section">
                  <h4>Basic Information</h4>
                  <div className="info-row">
                    <label>Case Number:</label>
                    <span>
                      <code>{selectedReport.caseNumber}</code>
                    </span>
                  </div>
                  <div className="info-row">
                    <label>Status:</label>
                    <span className={`status-badge ${getStatusColor(selectedReport.status)}`}>
                      {selectedReport.status}
                    </span>
                  </div>
                  <div className="info-row">
                    <label>Submitted:</label>
                    <span>{formatDate(selectedReport.createdAt)}</span>
                  </div>
                  {selectedReport.confirmedAt && (
                    <div className="info-row">
                      <label>Confirmed Submitted to Google:</label>
                      <span>{formatDate(selectedReport.confirmedAt)}</span>
                    </div>
                  )}
                </div>

                <div className="info-section">
                  <h4>Submitter Information</h4>
                  <div className="info-row">
                    <label>Name:</label>
                    <span>{selectedReport.name}</span>
                  </div>
                  <div className="info-row">
                    <label>Email:</label>
                    <span>{selectedReport.email}</span>
                  </div>
                  {selectedReport.organization && (
                    <div className="info-row">
                      <label>Organization:</label>
                      <span>{selectedReport.organization}</span>
                    </div>
                  )}
                </div>

                <div className="info-section">
                  <h4>App Information</h4>
                  <div className="info-row">
                    <label>Package Name(s):</label>
                    <span>
                      <div className="packages-list">
                        {selectedReport.packageNames.map((pkg, idx) => (
                          <code key={idx} className="package-item">{pkg}</code>
                        ))}
                      </div>
                    </span>
                  </div>
                  <div className="info-row">
                    <label>On Play Store:</label>
                    <span>{selectedReport.isOnPlayStore}</span>
                  </div>
                  <div className="info-row">
                    <label>SHA256 / Link:</label>
                    <span className="long-text">{selectedReport.sha256OrLink}</span>
                  </div>
                </div>

                <div className="info-section">
                  <h4>Malware Information</h4>
                  <div className="info-row">
                    <label>Suspected PHA Category:</label>
                    <span>
                      <span className="category-badge">{selectedReport.suspectedCategory}</span>
                    </span>
                  </div>
                  <div className="info-row">
                    <label>Issue Details:</label>
                    <div className="details-box">{selectedReport.details}</div>
                  </div>
                  <div className="info-row">
                    <label>Has Public Publication:</label>
                    <span>{selectedReport.hasPublicationLink}</span>
                  </div>
                </div>
              </div>

              {selectedReport.status === 'Pending' && (
                <div className="modal-actions">
                  <button
                    onClick={() => handleMarkAsSubmitted(selectedReport.caseNumber)}
                    className="confirm-btn"
                  >
                    ✓ Mark as Submitted to Google
                  </button>
                  <p className="action-hint">
                    Click this button after you've successfully submitted this report to Google's form
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleMalwareHistory;
